import type {
  CantonFixesOverview,
  CantonFixListItem,
  CantonFixStatus,
  CantonScan,
  ChargeFailureCategory,
  ChargeFailureDetail,
  ChargeFailureListItem,
  ChargeFailureStatus,
  KlaviyoSyncDetail,
  KlaviyoSyncListItem,
  LegacyMigrationDetail,
  LegacyMigrationListItem,
  LegacyMigrationStatus,
  Paginated,
  RechargeExportListItem,
  RechargeWebhookEventDetail,
  RechargeWebhookEventListItem,
  RechargeWebhookOutcome,
} from '@flux/shared';
import { apiFetch, downloadFromApi } from '@/lib/api/client.js';

/** Couche réseau de l'écran Abonnements (`06` §8). */

export interface WebhookEventListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  topic?: string[];
  outcome?: RechargeWebhookOutcome[];
  /** ISO-8601 UTC (début du jour parisien). */
  from?: string;
  /** ISO-8601 UTC (fin du jour parisien). */
  to?: string;
}

/** `GET /api/recharge/webhook-events` — journal des webhooks reçus (`06` §8.5). */
export function fetchWebhookEvents(
  params: WebhookEventListParams,
): Promise<Paginated<RechargeWebhookEventListItem>> {
  return apiFetch<Paginated<RechargeWebhookEventListItem>>('/api/recharge/webhook-events', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.topic && params.topic.length > 0 ? { topic: params.topic } : {}),
      ...(params.outcome && params.outcome.length > 0 ? { outcome: params.outcome } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });
}

/** `GET /api/recharge/webhook-events/:id` — un webhook avec son payload. */
export function fetchWebhookEvent(id: string): Promise<RechargeWebhookEventDetail> {
  return apiFetch<RechargeWebhookEventDetail>(
    `/api/recharge/webhook-events/${encodeURIComponent(id)}`,
  );
}

export interface ChargeFailureListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  category?: ChargeFailureCategory[];
  status?: ChargeFailureStatus[];
  /** Recherche par email (insensible à la casse côté serveur). */
  search?: string;
  /** ISO-8601 UTC (début du jour parisien). */
  from?: string;
  /** ISO-8601 UTC (fin du jour parisien). */
  to?: string;
}

/** `GET /api/recharge/charge-failures` — paiements échoués des renouvellements (`06` §8.1). */
export function fetchChargeFailures(
  params: ChargeFailureListParams,
): Promise<Paginated<ChargeFailureListItem>> {
  return apiFetch<Paginated<ChargeFailureListItem>>('/api/recharge/charge-failures', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.category && params.category.length > 0 ? { category: params.category } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });
}

/** `GET /api/recharge/charge-failures/:id` — un paiement échoué avec produits et trace Slack. */
export function fetchChargeFailure(id: string): Promise<ChargeFailureDetail> {
  return apiFetch<ChargeFailureDetail>(`/api/recharge/charge-failures/${encodeURIComponent(id)}`);
}

/** `GET /api/recharge/exports` — générations de l'export des abonnements actifs (`06` §8.4). */
export function fetchRechargeExports(params: {
  page: number;
  pageSize: number;
}): Promise<Paginated<RechargeExportListItem>> {
  return apiFetch<Paginated<RechargeExportListItem>>('/api/recharge/exports', {
    query: { page: params.page, pageSize: params.pageSize },
  });
}

/**
 * `POST /api/recharge/exports` — lance un export. 409 (message en français)
 * si un export tourne déjà ou si le token API manque.
 */
export function startRechargeExport(): Promise<RechargeExportListItem> {
  return apiFetch<RechargeExportListItem>('/api/recharge/exports', { method: 'POST' });
}

/** `GET /api/recharge/exports/:id/download` — le CSV, par une ancre authentifiée par cookie. */
export function downloadRechargeExport(id: string): void {
  downloadFromApi(`/api/recharge/exports/${encodeURIComponent(id)}/download`);
}

export interface CantonFixListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  status?: CantonFixStatus[];
  /** ISO-8601 UTC (début du jour parisien). */
  from?: string;
  /** ISO-8601 UTC (fin du jour parisien). */
  to?: string;
}

/** `GET /api/recharge/canton-fixes` — corrections de canton des adresses suisses (`06` §8.2). */
export function fetchCantonFixes(params: CantonFixListParams): Promise<Paginated<CantonFixListItem>> {
  return apiFetch<Paginated<CantonFixListItem>>('/api/recharge/canton-fixes', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });
}

/** `GET /api/recharge/canton-fixes/:id` — une correction de canton. */
export function fetchCantonFix(id: string): Promise<CantonFixListItem> {
  return apiFetch<CantonFixListItem>(`/api/recharge/canton-fixes/${encodeURIComponent(id)}`);
}

/** `GET /api/recharge/canton-fixes/overview` — adresses à traiter et dernière vérification. */
export function fetchCantonFixesOverview(): Promise<CantonFixesOverview> {
  return apiFetch<CantonFixesOverview>('/api/recharge/canton-fixes/overview');
}

/**
 * `POST /api/recharge/canton-scans` — lance la vérification de toutes les
 * adresses (`06` §3.3). 409 (message en français) si une vérification tourne
 * déjà ou si le token API manque.
 */
export function startCantonScan(body: { simulation: boolean }): Promise<CantonScan> {
  return apiFetch<CantonScan>('/api/recharge/canton-scans', { method: 'POST', body });
}

export interface LegacyMigrationListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  status?: LegacyMigrationStatus[];
  /** Recherche par identifiant d'abonnement ou email (insensible à la casse côté serveur). */
  search?: string;
  /** ISO-8601 UTC (début du jour parisien). */
  from?: string;
  /** ISO-8601 UTC (fin du jour parisien). */
  to?: string;
}

/** `GET /api/recharge/migrations` — migrations des abonnements legacy (`06` §8.3). */
export function fetchLegacyMigrations(
  params: LegacyMigrationListParams,
): Promise<Paginated<LegacyMigrationListItem>> {
  return apiFetch<Paginated<LegacyMigrationListItem>>('/api/recharge/migrations', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });
}

/** `GET /api/recharge/migrations/:id` — une migration avec sa composition, son calcul et sa trace Slack. */
export function fetchLegacyMigration(id: string): Promise<LegacyMigrationDetail> {
  return apiFetch<LegacyMigrationDetail>(`/api/recharge/migrations/${encodeURIComponent(id)}`);
}

/** `POST /api/recharge/migrations/:id/rollback` — remet l'abonnement d'origine chez Recharge (`06` §5.6). */
export function rollbackLegacyMigration(id: string): Promise<LegacyMigrationDetail> {
  return apiFetch<LegacyMigrationDetail>(`/api/recharge/migrations/${encodeURIComponent(id)}/rollback`, { method: 'POST' });
}

/** `GET /api/recharge/klaviyo-syncs` — synchronisations Klaviyo, sans leurs changements (`06` §11). */
export function fetchKlaviyoSyncs(params: { page: number; pageSize: number }): Promise<Paginated<KlaviyoSyncListItem>> {
  return apiFetch<Paginated<KlaviyoSyncListItem>>('/api/recharge/klaviyo-syncs', {
    query: { page: params.page, pageSize: params.pageSize },
  });
}

/** `GET /api/recharge/klaviyo-syncs/:id` — une synchronisation avec les profils modifiés. */
export function fetchKlaviyoSync(id: string): Promise<KlaviyoSyncDetail> {
  return apiFetch<KlaviyoSyncDetail>(`/api/recharge/klaviyo-syncs/${encodeURIComponent(id)}`);
}

/**
 * `POST /api/recharge/klaviyo-syncs` — lance une synchronisation. 409 (message
 * en français) si une autre tourne déjà ou si une clé manque.
 */
export function startKlaviyoSync(body: { simulation: boolean }): Promise<KlaviyoSyncDetail> {
  return apiFetch<KlaviyoSyncDetail>('/api/recharge/klaviyo-syncs', { method: 'POST', body });
}
